import React from "react";
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  CircularProgress,
  Alert,
} from "@mui/material";
import BaseModal from "./BaseModel/BaseModel";

/**
 * Modal component to display shipments list for a specific branch
 * @param {boolean} isOpen - Whether the modal is open
 * @param {function} onClose - Function to close the modal
 * @param {Array} shipments - Array of shipments to display
 * @param {boolean} isLoading - Loading state
 * @param {string} error - Error message if any
 * @param {string} branchName - Name of the branch
 * @param {string} branchType - Type of branch (loading/unloading)
 */
const ShipmentsListModal = ({
  isOpen,
  onClose,
  shipments = [],
  isLoading = false,
  error = null,
  branchName = "",
  branchType = "loading",
}) => {
  // Format date for display
  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  return (
    <BaseModal
      isOpen={isOpen}
      onClose={onClose}
      title={`${branchName} - ${branchType.charAt(0).toUpperCase() + branchType.slice(1)
        } Shipments`}
      width="90%"
      top="50%"
      left="50%"
    >
      <Box>
        {/* Loading State */}
        {isLoading && (
          <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
            <CircularProgress />
          </Box>
        )}

        {/* Error State */}
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {/* No Data State */}
        {!isLoading && !error && shipments.length === 0 && (
          <Alert severity="info" sx={{ mb: 2 }}>
            No shipments found for this branch.
          </Alert>
        )}

        {/* Shipments Table */}
        {!isLoading && !error && shipments.length > 0 && (
          <TableContainer component={Paper} sx={{ maxHeight: 500 }}>
            <Table stickyHeader size="small">
              <TableHead>
                <TableRow>
                  <TableCell>
                    <strong>Shipment ID</strong>
                  </TableCell>

                  <TableCell>
                    <strong>Source Branch</strong>
                  </TableCell>
                  <TableCell>
                    <strong>Destination Branch</strong>
                  </TableCell>
                  <TableCell>
                    <strong>Weight (kg)</strong>
                  </TableCell>

                  <TableCell>
                    <strong>Created Date</strong>
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {shipments.map((shipment, index) => (
                  <TableRow key={shipment._id || index} hover>
                    <TableCell>
                      <Typography variant="body2" fontWeight="medium">
                        {shipment.shipmentId || "N/A"}
                      </Typography>
                    </TableCell>

                    <TableCell>
                      <Typography variant="body2">
                        {shipment.sourceBranch?.name || "N/A"}
                      </Typography>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        display="block"
                      >
                        {shipment.sourceBranch?.code || ""}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {shipment.destinationBranch?.name || "N/A"}
                      </Typography>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        display="block"
                      >
                        {shipment.destinationBranch?.code || ""}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {shipment.weight ? `${shipment.weight} kg` : "N/A"}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {formatDate(shipment.createdAt)}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        {/* Summary */}
        {!isLoading && !error && shipments.length > 0 && (
          <Box sx={{
            mt: 3, p: 2, bgcolor: {
              bgcolor: (theme) =>
                theme.palette.action.hover
            }, borderRadius: 1
          }}>
            <Typography variant="h6" gutterBottom>
              Summary
            </Typography>
            <Typography variant="body2">
              <strong>Total Shipments:</strong> {shipments.length}
            </Typography>
            <Typography variant="body2">
              <strong>Total Weight:</strong>{" "}
              {shipments
                .reduce((sum, s) => sum + (Number(s.weight) || 0), 0)
                .toFixed(2)}{" "}
              kg
            </Typography>
          </Box>
        )}
      </Box>
    </BaseModal>
  );
};

export default ShipmentsListModal;
